// import React from "react";
// import {
//   RadarChart,
//   Radar,
//   PolarGrid,
//   PolarAngleAxis,
//   PolarRadiusAxis,
//   Legend,
//   ResponsiveContainer,
// } from "recharts";

// interface Competency {
//   competency: string;
//   rating: number;
// }

// const data: Competency[] = [
//   { competency: "Structured Thinking", rating: 3 },
//   { competency: "Communication Skill", rating: 4 },
//   { competency: "Prioritization", rating: 3 },
//   { competency: "Business Acumen", rating: 3 },
// ];

// const MAX_SCORE = 5;

// // Which labels are split into two lines?
// const multilineMap: Record<string, [string, string]> = {
//   "Business Acumen": ["Business", "Acumen"],
//   "Communication Skill": ["Communication", "Skill"],
// };

// const CoreCompetenciesRadar: React.FC = () => {
//   const renderCustomTick = (props: any) => {
//     const { payload, x, y, textAnchor } = props;
//     let dx = 0;

//     const index = payload.index;
//     const label = data[index].competency;
//     const rating = data[index].rating;

//     // Multi-line labels
//     if (multilineMap[label]) {
//       const [firstLine, secondLine] = multilineMap[label];

//       return (
//         <text x={x} y={y} textAnchor={textAnchor || "middle"} dx={dx}>
//           <tspan
//             style={{
//               fill: "#474A52",
//               fontFamily: "Product Sans",
//               fontSize: "12px",
//               fontWeight: 400,
//               lineHeight: "20px",
//             }}
//             x={x + dx}
//           >
//             {firstLine}
//           </tspan>
//           <tspan
//             style={{
//               fill: "#474A52",
//               fontFamily: "Product Sans",
//               fontSize: "12px",
//               fontWeight: 400,
//               lineHeight: "20px",
//             }}
//             x={x + dx}
//             dy={14}
//           >
//             {secondLine + " "}
//           </tspan>
//           {/* Score in bold/blue */}
//           <tspan
//             style={{
//               fill: "#1B58F4",
//               fontFamily: "Product Sans",
//               fontSize: "12px",
//               fontWeight: 700,
//               lineHeight: "20px",
//             }}
//           >
//             {rating}/{MAX_SCORE}
//           </tspan>
//         </text>
//       );
//     }

//     // Single-line labels
//     return (
//       <text x={x} y={y} textAnchor={textAnchor || "middle"} dx={dx}>
//         <tspan
//           style={{
//             fill: "#474A52",
//             fontFamily: "Product Sans",
//             fontSize: "12px",
//             fontWeight: 400,
//             lineHeight: "20px",
//           }}
//         >
//           {label + " "}
//         </tspan>
//         <tspan
//           style={{
//             fill: "#1B58F4",
//             fontFamily: "Product Sans",
//             fontSize: "12px",
//             fontWeight: 700,
//             lineHeight: "20px",
//           }}
//         >
//           {rating}/{MAX_SCORE}
//         </tspan>
//       </text>
//     );
//   };

//   return (
//     <div className="w-full h-full">
//       <ResponsiveContainer width="100%" height="100%">
//         <RadarChart
//           data={data}
//           cx="50%"
//           cy="50%"
//           outerRadius="60%"
//           margin={{ top: 20, bottom: 20, left: 5, right: 5 }}
//         >
//           {/* 1) Use polygon grid + radialLines={false} if you only want polygons, not radial spokes */}
//           <PolarGrid stroke="#E5E7EB" gridType="polygon" radialLines={false} />

//           {/* 2) Show custom multi-line label */}
//           <PolarAngleAxis tick={renderCustomTick} />

//           {/* 3) Domain [0,5] => 5 layers
//               tickCount={6} => 0..5 steps
//           */}
//           <PolarRadiusAxis
//             domain={[0, 5]}
//             tick={false}
//             tickCount={6}
//             axisLine={false}
//           />

//           <Radar
//             name="Competencies"
//             dataKey="rating"
//             stroke="#1B58F4"
//             fill="rgba(27, 88, 244, 0.30)"
//           />
//         </RadarChart>
//       </ResponsiveContainer>
//     </div>
//   );
// };

// export default CoreCompetenciesRadar;

import React from "react";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";

/**
 * 1) Radar data item shape passed in from the parent.
 *    E.g.: [{ name: "Structured Thinking", score: 3, maxScore: 5 }, ... ]
 */
interface RadarItem {
  name: string;
  score: number | string;
  maxScore: number | string;
}

/**
 * 2) Props for this component:
 *    We expect an array of RadarItem objects.
 */
interface CoreCompetenciesRadarProps {
  data: RadarItem[];
}

const CoreCompetenciesRadar: React.FC<CoreCompetenciesRadarProps> = ({
  data,
}) => {
  // 3) Convert each item into the Recharts-friendly shape:
  //    { competency: string; rating: number; maxScore: number }
  const mappedData = data.map((item) => {
    const parsedScore =
      typeof item.score === "string" ? parseFloat(item.score) : item.score;
    const parsedMaxScore =
      typeof item.maxScore === "string"
        ? parseFloat(item.maxScore)
        : item.maxScore;

    return {
      competency: item.name,
      rating: isNaN(parsedScore) ? 0 : parsedScore,
      maxScore: isNaN(parsedMaxScore) ? 100 : parsedMaxScore,
    };
  });

  // 4) Multi-line labels (if needed). Feel free to add more entries:
  const multilineMap: Record<string, [string, string]> = {
    "Business Acumen": ["Business", "Acumen"],
    "Communication Skill": ["Communication", "Skill"],
  };

  // 5) Define a custom tick function returning a valid SVG element
  const renderCustomTick = (props: any): React.ReactElement<SVGElement> => {
    const { payload, x, y, textAnchor } = props;

    // If there's no valid payload/index, return an empty <g> (not null)
    if (!payload || payload.index == null || !mappedData[payload.index]) {
      return <g />;
    }

    const { competency, rating, maxScore } = mappedData[payload.index];

    // Check if this competency needs multiline
    if (multilineMap[competency]) {
      const [firstLine, secondLine] = multilineMap[competency];

      return (
        <text x={x} y={y} textAnchor={textAnchor || "middle"}>
          <tspan
            style={{
              fill: "#474A52",
              fontFamily: "Product Sans",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "20px",
            }}
            x={x}
          >
            {firstLine}
          </tspan>
          <tspan
            style={{
              fill: "#474A52",
              fontFamily: "Product Sans",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "20px",
            }}
            x={x}
            dy={14}
          >
            {secondLine + " "}
          </tspan>
          {/* Commented out score display */}
          {/* <tspan
            style={{
              fill: "var(--color-primary)",
              fontFamily: "Product Sans",
              fontSize: "12px",
              fontWeight: 700,
              lineHeight: "20px",
            }}
          >
            {rating}/{maxScore}
          </tspan> */}
        </text>
      );
    }

    // Single-line label
    return (
      <text x={x} y={y} textAnchor={textAnchor || "middle"}>
        <tspan
          style={{
            fill: "#474A52",
            fontFamily: "Product Sans",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "20px",
          }}
          x={x}
        >
          {competency}
        </tspan>
        {/* Commented out score display */}
        {/* <tspan
          style={{
            fill: "var(--color-primary)",
            fontFamily: "Product Sans",
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "20px",
          }}
          x={x}
          dy={14}
        >
          {rating}/{maxScore}
        </tspan> */}
      </text>
    );
  };

  return (
    <div className="w-[300px] h-full">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart
          data={mappedData}
          cx="50%"
          cy="50%"
          outerRadius="60%"
          margin={{ top: 20, bottom: 20, left: 5, right: 5 }}
        >
          {/* Polygon grid + no radial lines */}
          <PolarGrid stroke="#E5E7EB" gridType="polygon" radialLines={false} />

          {/* Use our custom multi-line label */}
          <PolarAngleAxis tick={renderCustomTick} />

          {/* Domain [0..5], no visible ticks, 6 steps (0..5) */}
          <PolarRadiusAxis
            domain={[0, 100]}
            tick={false}
            tickCount={6}
            axisLine={false}
          />

          <Radar
            name="Competencies"
            dataKey="rating"
            stroke="var(--color-primary)"
            fill="rgba(27, 88, 244, 0.30)"
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CoreCompetenciesRadar;
